import React, { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';

import { BasePopupActionPayload, BasePopupProps } from 'contexts/PopupContext';

import { Funnels } from 'helpers/funnels/funnels';
import { useStopExitPopupAnalytics } from 'hooks/analytics/useStopExitPopupAnalytics';
import { usePremiumPackPrice } from 'hooks/usePremiumPackPrice';
import { QueryKeys } from 'hooks/useSystemSearchQueries';
import { getTrialPackage } from 'store/payment/selectors';

import { BaseButton, ButtonTypes } from 'components/base/BaseButton';
import { Popup } from 'components/UI/Popup';

import { getImageByLang } from './imgHelper';

import css from './stopExitPopup.module.sass';

type ActionsPayload = BasePopupActionPayload<'close'>;

type Props = BasePopupProps<null, (action: ActionsPayload) => void>;

export const StopExitPopup: React.FC<Props> = ({ onAction }) => {
  const { t } = useTranslation('app');

  const { search } = useLocation();
  const history = useHistory();

  const trialPackage = useSelector(getTrialPackage);

  const [imgSrc, setImgSrc] = useState<string>();

  const packagePrice = usePremiumPackPrice(trialPackage);

  const {
    trackStopExitPopupClick,
    trackStopExitPopupClose,
    trackStopExitPopupShow,
  } = useStopExitPopupAnalytics();

  const handleClose = useCallback(() => {
    trackStopExitPopupClose();
    onAction({ type: 'close' });
  }, [onAction, trackStopExitPopupClose]);

  const handleClick = () => {
    const params = queryString.parse(search);
    params[QueryKeys.Funnel] = Funnels.StopExit;

    history.replace({
      pathname: `/payment/${trialPackage?.id}`,
      search: queryString.stringify(params),
    });

    trackStopExitPopupClick();

    handleClose();
  };

  const getImg = async () => {
    const img = await getImageByLang();
    setImgSrc(img);
  };

  useEffect(() => {
    getImg();
  }, []);

  useEffect(() => {
    trackStopExitPopupShow();
  }, [trackStopExitPopupShow]);

  useEffect(() => {
    if (!trialPackage) {
      handleClose();
    }
  }, [handleClose, trialPackage]);

  if (!trialPackage) {
    return null;
  }

  return (
    <Popup popupClassName={css.popup} onClose={handleClose}>
      {imgSrc && <img src={imgSrc} alt="trial" className={css.img} />}
      <p className={css.headline}>{t('stopExitPopup.tryPremium')}</p>
      <p className={css.text}>
        <Trans
          i18nKey="stopExitPopup.packName"
          ns="app"
          values={{
            displayName: trialPackage.display_name,
          }}
          components={{
            span: <span className={css.textBold} />,
          }}
        />
      </p>
      <p className={css.price}>{packagePrice?.priceByPeriod}</p>
      <div className={css.btnBox}>
        <BaseButton
          type={ButtonTypes.Accent}
          className={css.btn}
          onClick={handleClick}
        >
          {t('stopExitPopup.startTrial')}
        </BaseButton>
      </div>
    </Popup>
  );
};
